/**
 * DeviceTrustManager - Orchestrates device trust and cross-signing operations
 *
 * Handles:
 * - Cross-signing bootstrap after recovery
 * - Automatic device trust when cross-signing keys available
 * - Fallback to interactive verification (QR/SAS)
 * - Error handling and retry logic
 */

import matrixService from '../services/matrixService';
import logger from '../utils/logger';
import { trustEventEmitter, TrustEvents, DeviceTrustError } from './events';

const BOOTSTRAP_TIMEOUT = 15000; // 15 seconds
const RETRY_DELAY = 2000; // 2 seconds
const MAX_RETRIES = 3;

class DeviceTrustManager {
  constructor() {
    this.isBootstrapping = false;
    this.bootstrapPromise = null;
    this.retryCount = 0;
    this.abortController = null;
  }

  /**
   * Run device trust automation after recovery completes
   * @returns {Promise<Object>} Result with crossSigningPresent, trusted, fallbackRequired flags
   */
  async runAfterRecovery() {
    logger.debug('DeviceTrustManager: Running post-recovery device trust automation');

    try {
      // Ensure cross-signing is bootstrapped
      const bootstrapResult = await this.ensureCrossSigningBootstrapped();

      if (!bootstrapResult.crossSigningPresent) {
        logger.warn('DeviceTrustManager: Cross-signing not available after bootstrap');
        return {
          crossSigningPresent: false,
          trusted: false,
          fallbackRequired: true,
        };
      }

      // Try to auto-trust the current device
      const trusted = await this.trustCurrentDeviceIfNeeded();

      return {
        crossSigningPresent: true,
        trusted,
        fallbackRequired: !trusted,
      };
    } catch (error) {
      logger.error('DeviceTrustManager: runAfterRecovery failed:', error);
      const errorCode = this.mapErrorToCode(error);

      trustEventEmitter.emitTrustEvent(TrustEvents.BOOTSTRAP_ERROR, {
        error: errorCode,
        message: error.message,
      });

      return {
        crossSigningPresent: false,
        trusted: false,
        fallbackRequired: true,
        error: errorCode,
      };
    }
  }

  /**
   * Ensure cross-signing is bootstrapped (keys cached/uploaded)
   * @returns {Promise<Object>} Result with crossSigningPresent flag
   */
  async ensureCrossSigningBootstrapped() {
    // Guard against concurrent bootstrap attempts
    if (this.isBootstrapping && this.bootstrapPromise) {
      logger.debug('DeviceTrustManager: Bootstrap already in progress, waiting...');
      return this.bootstrapPromise;
    }

    this.isBootstrapping = true;
    this.bootstrapPromise = this._doBootstrap();

    try {
      const result = await this.bootstrapPromise;
      return result;
    } finally {
      this.isBootstrapping = false;
      this.bootstrapPromise = null;
    }
  }

  /**
   * Internal bootstrap implementation with timeout
   * @private
   */
  async _doBootstrap() {
    const client = matrixService.client;

    // Check if client is available
    if (!client) {
      logger.warn('DeviceTrustManager: No client available');
      return { crossSigningPresent: false, error: DeviceTrustError.NO_CRYPTO };
    }

    // Use getCrypto() to check crypto availability (proper way for Rust crypto SDK)
    // Note: isCryptoEnabled() may return false with Rust crypto even when getCrypto() works
    const crypto = client.getCrypto?.();

    if (!crypto) {
      logger.warn('DeviceTrustManager: Crypto API not available');
      return { crossSigningPresent: false, error: DeviceTrustError.NO_CRYPTO };
    }

    trustEventEmitter.emitTrustEvent(TrustEvents.BOOTSTRAP_START, {});

    // Create abort controller for timeout
    this.abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      this.abortController.abort();
    }, BOOTSTRAP_TIMEOUT);

    try {
      logger.debug('DeviceTrustManager: Bootstrapping cross-signing...');

      // Bootstrap cross-signing with setupNewCrossSigning=false
      // This ensures we only cache/upload existing keys, not create new ones
      // CRITICAL: authUploadDeviceSigningKeys callback is needed to upload device signatures
      await crypto.bootstrapCrossSigning({
        setupNewCrossSigning: false,
        authUploadDeviceSigningKeys: async (makeRequest) => {
          logger.debug('DeviceTrustManager: Uploading device signing keys');
          await makeRequest();
        },
      });

      clearTimeout(timeoutId);

      // Check if cross-signing keys are present
      const crossSigningPresent = await this._checkCrossSigningKeys(crypto);

      // Log clear status for debugging recovery key transfer issues
      if (crossSigningPresent) {
        logger.info('DeviceTrustManager: ✅ Cross-signing PUBLIC keys available on device');
        logger.info('DeviceTrustManager: ✅ Cross-signing PRIVATE keys cached from SSSS');
      } else {
        logger.warn('DeviceTrustManager: ❌ Cross-signing PUBLIC keys NOT available on device');
        logger.warn('DeviceTrustManager: Recovery key transfer may NOT work - check if keys were published to server');
      }

      trustEventEmitter.emitTrustEvent(TrustEvents.BOOTSTRAP_DONE, {
        crossSigningPresent,
      });

      logger.debug('DeviceTrustManager: Bootstrap complete, cross-signing present:', crossSigningPresent);

      return { crossSigningPresent };
    } catch (error) {
      clearTimeout(timeoutId);

      if (this.abortController?.signal.aborted) {
        logger.error('DeviceTrustManager: Bootstrap timeout');
        throw new Error('Bootstrap operation timed out');
      }

      logger.error('DeviceTrustManager: Bootstrap failed:', error);
      throw error;
    } finally {
      this.abortController = null;
    }
  }

  /**
   * Check if cross-signing keys are present
   * @private
   */
  async _checkCrossSigningKeys(crypto) {
    try {
      // Try different methods to check for cross-signing keys
      if (crypto.getCrossSigningStatus) {
        const status = await crypto.getCrossSigningStatus();
        return status?.publicKeysOnDevice || status?.hasKeys || false;
      }

      if (crypto.getCrossSigningKeyId) {
        // Check for master key as indicator
        const masterKeyId = await crypto.getCrossSigningKeyId('master');
        return !!masterKeyId;
      }

      // Fallback: check via Matrix service
      const crossSigningStatus = await matrixService.getCrossSigningStatus();
      return crossSigningStatus?.ready || false;
    } catch (error) {
      logger.warn('DeviceTrustManager: Could not check cross-signing keys:', error);
      return false;
    }
  }

  /**
   * Trust the current device if needed
   * @returns {Promise<boolean>} True if device is trusted
   */
  async trustCurrentDeviceIfNeeded() {
    const client = matrixService.client;
    if (!client) {
      logger.error('DeviceTrustManager: No Matrix client available');
      return false;
    }

    const userId = client.getUserId();
    const deviceId = client.getDeviceId();

    if (!userId || !deviceId) {
      logger.error('DeviceTrustManager: No user/device ID available');
      return false;
    }

    const crypto = client.getCrypto?.();
    if (!crypto) {
      logger.error('DeviceTrustManager: Crypto API not available');
      return false;
    }

    try {
      // Check current verification status
      const status = await crypto.getDeviceVerificationStatus(userId, deviceId);

      if (status?.isVerified() === true) {
        logger.debug('DeviceTrustManager: Device already verified');
        trustEventEmitter.emitTrustEvent(TrustEvents.DEVICE_UNCHANGED, {
          userId,
          deviceId,
        });
        return true;
      }

      // Attempt to trust the device
      trustEventEmitter.emitTrustEvent(TrustEvents.DEVICE_TRUSTING, {
        userId,
        deviceId,
      });

      logger.debug('DeviceTrustManager: Trusting device...');

      // Different SDKs may have different methods
      if (crypto.setDeviceVerified) {
        await crypto.setDeviceVerified(userId, deviceId, true);
      } else if (crypto.trustDevice) {
        await crypto.trustDevice(userId, deviceId);
      } else if (matrixService.setDeviceVerified) {
        await matrixService.setDeviceVerified(userId, deviceId, true);
      } else {
        logger.warn('DeviceTrustManager: No method available to trust device');
        return false;
      }

      // Re-check verification status
      const newStatus = await crypto.getDeviceVerificationStatus(userId, deviceId);
      const trusted = newStatus?.isVerified() === true;

      if (trusted) {
        logger.info('DeviceTrustManager: Device successfully trusted');
        trustEventEmitter.emitTrustEvent(TrustEvents.DEVICE_TRUSTED, {
          userId,
          deviceId,
        });
      } else {
        logger.warn('DeviceTrustManager: Device trust failed to verify');
        trustEventEmitter.emitTrustEvent(TrustEvents.DEVICE_ERROR, {
          userId,
          deviceId,
          error: DeviceTrustError.UNKNOWN,
        });
      }

      return trusted;
    } catch (error) {
      logger.error('DeviceTrustManager: Failed to trust device:', error);

      const errorCode = this.mapErrorToCode(error);
      trustEventEmitter.emitTrustEvent(TrustEvents.DEVICE_ERROR, {
        userId,
        deviceId,
        error: errorCode,
        message: error.message,
      });

      // If cross-signing keys are missing, we need fallback verification
      if (errorCode === DeviceTrustError.NO_CROSS_SIGNING) {
        return false;
      }

      throw error;
    }
  }

  /**
   * Retry bootstrap operation on network recovery
   * @returns {Promise<Object>} Bootstrap result
   */
  async retryBootstrap() {
    if (this.retryCount >= MAX_RETRIES) {
      logger.warn('DeviceTrustManager: Max retries reached');
      return { crossSigningPresent: false, error: DeviceTrustError.NETWORK };
    }

    this.retryCount++;
    logger.debug(`DeviceTrustManager: Retry attempt ${this.retryCount}/${MAX_RETRIES}`);

    // Wait before retry
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));

    try {
      const result = await this.ensureCrossSigningBootstrapped();
      this.retryCount = 0; // Reset on success
      return result;
    } catch (error) {
      logger.error('DeviceTrustManager: Retry failed:', error);
      throw error;
    }
  }

  /**
   * Map error to DeviceTrustError code
   * @private
   */
  mapErrorToCode(error) {
    const message = error.message?.toLowerCase() || '';
    const code = error.errcode || error.code;

    if (message.includes('network') || message.includes('fetch') || code === 'M_UNKNOWN') {
      return DeviceTrustError.NETWORK;
    }

    if (message.includes('unauthorized') || code === 'M_UNAUTHORIZED' || code === 401) {
      return DeviceTrustError.UNAUTHORIZED;
    }

    if (message.includes('cross') || message.includes('signing') || message.includes('keys')) {
      return DeviceTrustError.NO_CROSS_SIGNING;
    }

    if (message.includes('crypto') || message.includes('not enabled')) {
      return DeviceTrustError.NO_CRYPTO;
    }

    if (message.includes('timeout')) {
      return DeviceTrustError.TIMEOUT;
    }

    if (message.includes('already') || message.includes('verified')) {
      return DeviceTrustError.ALREADY_VERIFIED;
    }

    return DeviceTrustError.UNKNOWN;
  }

  /**
   * Check if the current device is verified
   * @returns {Promise<boolean>} True if current device is verified
   */
  async isCurrentDeviceVerified() {
    const client = matrixService.client;
    if (!client) {
      logger.error('DeviceTrustManager: No Matrix client available');
      return false;
    }

    const userId = client.getUserId();
    const deviceId = client.getDeviceId();

    if (!userId || !deviceId) {
      logger.error('DeviceTrustManager: No user/device ID available');
      return false;
    }

    const crypto = client.getCrypto?.();
    if (!crypto) {
      logger.error('DeviceTrustManager: Crypto API not available');
      return false;
    }

    try {
      // Check device verification status
      const status = await crypto.getDeviceVerificationStatus(userId, deviceId);
      
      // Device is verified if:
      // - signed by owner's cross-signing key (signedByOwner)
      // - OR has cross-signing verification
      const isVerified = !!(
        status?.signedByOwner ||
        status?.crossSigningVerified ||
        status?.isVerified()
      );

      logger.debug('DeviceTrustManager: Current device verified:', isVerified);
      return isVerified;
    } catch (error) {
      logger.warn('DeviceTrustManager: Could not check device verification:', error);
      return false;
    }
  }

  /**
   * Reset state (useful for testing or logout)
   */
  reset() {
    this.isBootstrapping = false;
    this.bootstrapPromise = null;
    this.retryCount = 0;
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
}

// Create singleton instance
const deviceTrustManager = new DeviceTrustManager();
export default deviceTrustManager;

// Also export class for testing
export { DeviceTrustManager };